import { useState, useEffect, useMemo, useCallback } from 'react';

/**
 * DeviceAssignDrawer — Assign devices to a device careflow.
 *
 * Uses the same picker pattern as AreaGroupDrawer / UserGroupDrawer:
 * search input, Select All / Clear / Show Selected action buttons,
 * scrollable checkbox list.
 *
 * Props:
 *   open          — drawer visibility
 *   onClose       — close callback
 *   onSave        — save callback (careflowRow, selectedDeviceIds[])
 *   saving        — boolean
 *   careflowRow   — the careflow row being edited
 *   devices       — available devices array
 */
const DeviceAssignDrawer = ({ open, onClose, onSave, saving, careflowRow, devices }) => {
    const [selectedIds, setSelectedIds] = useState([]);
    const [search, setSearch] = useState('');
    const [showSelectedOnly, setShowSelectedOnly] = useState(false);

    // Initialize selected IDs from careflow references when drawer opens
    useEffect(() => {
        if (open && careflowRow) {
            const refIds = careflowRow.references
                ? careflowRow.references.split(',').filter(Boolean)
                : [];
            setSelectedIds(refIds);
            setSearch('');
            setShowSelectedOnly(false);
        }
    }, [open, careflowRow]);

    // Filter and sort devices
    const filteredDevices = useMemo(() => {
        let list = [...(devices || [])];
        if (showSelectedOnly) {
            list = list.filter((d) => selectedIds.includes(String(d.device_id)));
        }
        if (search.trim()) {
            const term = search.toLowerCase();
            list = list.filter(
                (d) =>
                    (d.device_name || '').toLowerCase().includes(term) ||
                    (d.descriptions || '').toLowerCase().includes(term) ||
                    (d.manufacture_uuid || '').toLowerCase().includes(term)
            );
        }
        list.sort((a, b) =>
            (a.device_name || '').localeCompare(b.device_name || '', undefined, { sensitivity: 'base' })
        );
        return list;
    }, [devices, search, showSelectedOnly, selectedIds]);

    const handleToggle = useCallback((deviceId) => {
        const strId = String(deviceId);
        setSelectedIds((prev) =>
            prev.includes(strId) ? prev.filter((id) => id !== strId) : [...prev, strId]
        );
    }, []);

    const handleSelectAll = useCallback(() => {
        const allFilteredIds = filteredDevices.map((d) => String(d.device_id));
        setSelectedIds((prev) => [...new Set([...prev, ...allFilteredIds])]);
    }, [filteredDevices]);

    const handleClear = useCallback(() => {
        setSelectedIds([]);
    }, []);

    const handleSave = () => {
        onSave(careflowRow, selectedIds);
    };

    return (
        <>
            {open ? <div className="dp-drawer-backdrop" onClick={onClose} /> : null}
            <div className={`dp-drawer${open ? ' is-open' : ''}`}>
                <div className="dp-drawer__header">
                    <h2 className="dp-drawer__title">Assign Careflow Devices</h2>
                    <button className="dp-drawer__close" onClick={onClose}>
                        <i className="fe fe-x" />
                    </button>
                </div>

                <div className="dp-drawer__body">
                    {careflowRow ? (
                        <p style={{ marginBottom: 'var(--space-3, 0.75rem)', color: 'var(--color-text-muted, #6b7280)', fontSize: 'var(--font-size-sm, 0.875rem)' }}>
                            Careflow: <strong>{careflowRow.careflow_description || '(unnamed)'}</strong>
                        </p>
                    ) : null}

                    {/* Picker */}
                    <div className="ws-device-picker">
                        <div className="ws-device-picker__header">
                            <p className="dp-drawer__label" style={{ marginBottom: 0 }}>
                                Devices
                                <span className="ws-device-picker__count">
                                    ({selectedIds.length} selected)
                                </span>
                            </p>
                        </div>

                        {/* Search */}
                        <div className="ws-device-picker__search">
                            <i className="fe fe-search ws-device-picker__search-icon" aria-hidden="true" />
                            <input
                                type="text"
                                placeholder="Search devices..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="ws-device-picker__search-input"
                            />
                        </div>

                        {/* Select All / Clear / Show Selected */}
                        <div className="ws-device-picker__actions">
                            <button
                                type="button"
                                onClick={handleSelectAll}
                                className="ws-device-picker__action-btn"
                            >
                                Select All
                            </button>
                            <button
                                type="button"
                                onClick={handleClear}
                                className="ws-device-picker__action-btn"
                            >
                                Clear
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowSelectedOnly((prev) => !prev)}
                                className={`ws-device-picker__action-btn${showSelectedOnly ? ' ws-device-picker__action-btn--active' : ''}`}
                            >
                                Show Selected
                            </button>
                        </div>

                        {/* Device list */}
                        <div className="ws-device-picker__list">
                            {filteredDevices.length === 0 ? (
                                <p className="ws-device-picker__empty">
                                    {showSelectedOnly ? 'No selections.' : 'No devices found'}
                                </p>
                            ) : (
                                filteredDevices.map((d) => {
                                    const dId = String(d.device_id);
                                    const isChecked = selectedIds.includes(dId);
                                    return (
                                        <label
                                            key={dId}
                                            className={`ws-device-picker__item${isChecked ? ' is-selected' : ''}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isChecked}
                                                onChange={() => handleToggle(d.device_id)}
                                                className="ap-checkbox"
                                            />
                                            <div className="ws-device-picker__item-info">
                                                <span className="ws-device-picker__item-name">{d.device_name || '—'}</span>
                                                <span className="ws-device-picker__item-desc">
                                                    {d.descriptions}{d.descriptions && d.manufacture_uuid ? ' · ' : ''}{d.manufacture_uuid || ''}
                                                </span>
                                            </div>
                                        </label>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>

                <div className="dp-drawer__footer">
                    <button className="dp-btn dp-btn--cancel" onClick={onClose}>Cancel</button>
                    <button className="dp-btn dp-btn--primary" disabled={saving} onClick={handleSave}>
                        {saving ? 'Saving...' : 'Save Devices'}
                    </button>
                </div>
            </div>
        </>
    );
};

export default DeviceAssignDrawer;
